import React, { useState, useMemo } from 'react';
import { Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { PE_DATES } from './utils/peDates';

// ─── PALETTE ─────────────────────────────────────────────────
const C = {
  bg: '#030b16', bgMid: '#060f1e', bgCard: '#091729', bgPanel: '#071323',
  navy: '#0d1c35', navyMid: '#132240',
  cyan: '#00b8cc', cyanDim: 'rgba(0,184,204,0.12)', cyanBorder: 'rgba(0,184,204,0.22)',
  orange: '#e85d26', orangeDim: 'rgba(232,93,38,0.12)',
  white: '#f0f4f8', gray: '#7a8799', grayLt: '#b0bcc8',
  border: 'rgba(255,255,255,0.07)', borderMid: 'rgba(255,255,255,0.04)',
  green: '#22c55e', purple: '#8b5cf6', amber: '#f59e0b', red: '#ef4444',
  lavender: '#a78bfa',
};

// ─── CICLOS 2026 ─────────────────────────────────────────────
interface Cycle {
  n: number;
  label: string;
  months: number[];
  se: string;
  color: string;
}
const CYCLES: Cycle[] = [
  { n: 1, label: '1° Ciclo', months: [0, 1],   se: 'SE 1 – 8',   color: C.cyan },
  { n: 2, label: '2° Ciclo', months: [2, 3],   se: 'SE 9 – 17',  color: C.green },
  { n: 3, label: '3° Ciclo', months: [4, 5],   se: 'SE 17 – 26', color: C.amber },
  { n: 4, label: '4° Ciclo', months: [6, 7],   se: 'SE 26 – 35', color: C.orange },
  { n: 5, label: '5° Ciclo', months: [8, 9],   se: 'SE 35 – 43', color: C.purple },
  { n: 6, label: '6° Ciclo', months: [10, 11], se: 'SE 44 – 52', color: C.cyan },
];
const cycleOf = (monthIdx: number): Cycle =>
  CYCLES.find(c => c.months.includes(monthIdx)) || CYCLES[0];

const MONTHS     = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const MONTHS_LONG = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DPM = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const SE_START = new Date(2026, 0, 4);
function seFor(monthIdx: number, day: number): number | null {
  const d = new Date(2026, monthIdx, day);
  const diffDays = Math.floor((d.getTime() - SE_START.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return null;
  return Math.floor(diffDays / 7) + 1;
}

// ─── FERIADOS BA 2026 ────────────────────────────────────────
const HOLIDAYS: { [key: string]: string } = {
  '0-1':  'Confraternização',
  '1-16': 'Carnaval',
  '1-17': 'Carnaval',
  '1-18': 'Quarta de Cinzas',
  '3-3':  'Sexta-feira Santa',
  '3-21': 'Tiradentes',
  '4-1':  'Dia do Trabalho',
  '5-4':  'Corpus Christi',
  '6-2':  'Independência da Bahia',
  '8-7':  'Independência',
  '9-12': 'N. Sra. Aparecida',
  '9-28': 'Servidor Público',
  '10-2': 'Finados',
  '10-15':'Proclamação da República',
  '10-20':'Consciência Negra',
  '11-8': 'N. Sra. Conceição (BA)',
  '11-25':'Natal',
};

const LIRAA_WEEKS = new Set<number>([4, 5, 6, 27, 28, 29]);
// PE_DATES importado de utils/peDates.ts

interface DayCell { day: number; monthIdx: number; isPad: boolean; }
interface Week { se: number | null; days: DayCell[]; }

function buildMonth(mIdx: number): Week[] {
  const firstDow = new Date(2026, mIdx, 1).getDay();
  const dim = DPM[mIdx];
  const weeks: Week[] = [];
  let cur: DayCell[] = [];
  const prevMIdx = (mIdx + 11) % 12;
  const prevDim  = DPM[prevMIdx];
  for (let i = 0; i < firstDow; i++) {
    cur.push({ day: prevDim - firstDow + 1 + i, monthIdx: prevMIdx, isPad: true });
  }
  for (let d = 1; d <= dim; d++) {
    cur.push({ day: d, monthIdx: mIdx, isPad: false });
    if (cur.length === 7) { weeks.push({ se: 0, days: cur }); cur = []; }
  }
  if (cur.length > 0) {
    const nextMIdx = (mIdx + 1) % 12;
    let nd = 1;
    while (cur.length < 7) cur.push({ day: nd++, monthIdx: nextMIdx, isPad: true });
    weeks.push({ se: 0, days: cur });
  }
  return weeks.map(w => {
    const sun = w.days[0];
    return { se: seFor(sun.monthIdx, sun.day), days: w.days };
  });
}

// ─── ANIMAÇÕES CSS ────────────────────────────────────────────
const ANIMATIONS_CSS = `
  @keyframes ce-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  @keyframes ce-fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ce-scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes ce-todayRing {
    0%   { box-shadow: 0 0 0 0 rgba(0,184,204,0.55), 0 0 18px rgba(0,184,204,0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(0,184,204,0.00), 0 0 28px rgba(0,184,204,0.70); }
    100% { box-shadow: 0 0 0 0 rgba(0,184,204,0.00), 0 0 18px rgba(0,184,204,0.55); }
  }
  @keyframes ce-todayBadgePulse {
    0%,100% { transform: translateX(-50%) translateY(0); opacity: 1; }
    50%     { transform: translateX(-50%) translateY(-2px); opacity: 0.85; }
  }
  @keyframes ce-todayCorner { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
  @media print {
    body { background:#fff !important; color:#000 !important; }
    .ce-no-print { display:none !important; }
    .ce-cal-card { background:#fff !important; border:1px solid #000 !important; box-shadow:none !important; color:#000 !important; }
    .ce-cal-day  { color:#000 !important; }
    .ce-cal-grid { break-inside: avoid; }
  }
`;

// ─── BACKGROUND ──────────────────────────────────────────────
const Background: React.FC = () => (
  <div className="ce-no-print" style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0, overflow:'hidden' }}>
    <div style={{ position:'absolute', inset:0,
      background:`
        radial-gradient(ellipse 80% 50% at 10% 0%, rgba(0,184,204,0.06) 0%, transparent 60%),
        radial-gradient(ellipse 60% 40% at 90% 100%, rgba(232,93,38,0.05) 0%, transparent 60%),
        radial-gradient(ellipse 40% 60% at 50% 50%, rgba(13,28,53,0.8) 0%, transparent 100%),
        #030b16` }}/>
    <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:0.025 }}>
      <defs>
        <pattern id="ce-dotgrid" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.2" fill={C.cyan}/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#ce-dotgrid)"/>
    </svg>
    <div style={{ position:'absolute', left:0, right:0, height:120,
      background:`linear-gradient(180deg, transparent, rgba(0,184,204,0.025) 50%, transparent)`,
      animation:'ce-scanline 12s linear infinite' }}/>
  </div>
);

// ─── PAGE HEADER ─────────────────────────────────────────────
const PageHeader: React.FC<{ currentSE: number | null; today: Date; onVoltar: () => void }> =
  ({ currentSE, today, onVoltar }) => {
  const dateStr = today.toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long', year:'numeric' });
  const dow3 = dateStr.split(',')[0].slice(0, 3).toUpperCase() + '.';
  return (
    <div style={{
      position:'relative', overflow:'hidden',
      padding:'28px 40px 24px',
      background:`linear-gradient(120deg, #060f1e 0%, #0a1828 55%, #06101e 100%)`,
      borderBottom:`1px solid ${C.border}`,
    }}>
      <svg style={{ position:'absolute', right:0, top:0, opacity:0.05, pointerEvents:'none' }}
        width="500" height="160" viewBox="0 0 500 160">
        {Array.from({length:9}).map((_,i)=><line key={`v${i}`} x1={i*60} y1="0" x2={i*60} y2="160" stroke={C.cyan} strokeWidth="1"/>)}
        {Array.from({length:5}).map((_,i)=><line key={`h${i}`} x1="0" y1={i*40} x2="500" y2={i*40} stroke={C.cyan} strokeWidth="1"/>)}
      </svg>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:24 }}>
        <div>
          <button onClick={onVoltar} className="ce-no-print" style={{
            display:'inline-flex', alignItems:'center', gap:6,
            padding:'6px 12px', marginBottom:14, borderRadius:6,
            border:`1px solid ${C.border}`,
            background:'rgba(255,255,255,0.03)',
            color:C.grayLt, fontSize:12, fontWeight:500, cursor:'pointer',
            fontFamily:"'Inter', sans-serif",
          }}>
            <ArrowBackIcon style={{ fontSize:14 }}/>
            Voltar
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:6 }}>
            <div style={{ width:42, height:42, borderRadius:10,
              background:`linear-gradient(135deg, ${C.cyan}30, ${C.cyan}08)`,
              border:`1px solid ${C.cyanBorder}`,
              display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="3" y="5" width="16" height="14" rx="2" stroke={C.cyan} strokeWidth="1.4"/>
                <path d="M3 9h16" stroke={C.cyan} strokeWidth="1.4"/>
                <path d="M7 3v4M15 3v4" stroke={C.cyan} strokeWidth="1.4" strokeLinecap="round"/>
                <circle cx="7" cy="13" r="1.2" fill={C.cyan}/>
                <circle cx="11" cy="13" r="1.2" fill={C.orange}/>
                <circle cx="15" cy="13" r="1.2" fill={C.cyan}/>
              </svg>
            </div>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <h1 style={{ fontSize:26, fontWeight:800, color:C.white, letterSpacing:'-0.02em', lineHeight:1, fontFamily:"'Inter', sans-serif", margin:0 }}>
                  Calendário Epidemiológico
                </h1>
                <span style={{ width:6, height:6, borderRadius:'50%', background:C.cyan, animation:'ce-pulse 2.5s infinite', boxShadow:`0 0 6px ${C.cyan}` }}/>
              </div>
              <div style={{ fontSize:12, color:C.gray, fontFamily:"'JetBrains Mono', monospace", marginTop:6 }}>
                Bahia · SESAB · DIVEP · COOTV — Ano de 2026
              </div>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:10, flexShrink:0 }}>
          {[
            { label:'HOJE',     value: dow3,                     sub: today.toLocaleDateString('pt-BR'), color: C.white },
            { label:'SE ATUAL', value: 'SE ' + (currentSE || '—'), sub: 'Semana Epi.',                  color: C.cyan  },
            { label:'ANO',      value: '2026',                   sub: 'Calendário',                     color: C.white },
          ].map(k => (
            <div key={k.label} style={{
              padding:'12px 18px', borderRadius:10,
              background:'rgba(0,184,204,0.04)',
              border:`1px solid ${C.cyanBorder}`,
              minWidth:104, textAlign:'left',
            }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', color:C.gray, fontFamily:"'JetBrains Mono', monospace" }}>{k.label}</div>
              <div style={{ fontSize:22, fontWeight:800, color:k.color, fontFamily:"'JetBrains Mono', monospace", lineHeight:1, marginTop:5 }}>{k.value}</div>
              <div style={{ fontSize:9.5, color:C.gray, marginTop:4 }}>{k.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── LEGEND ──────────────────────────────────────────────────
const Legend: React.FC<{ filter: string | null; setFilter: (f: string | null) => void }> =
  ({ filter, setFilter }) => {
  const items = [
    { key:'pe',    label:'Pontos Estratégicos', color: C.lavender },
    { key:'liraa', label:'LIRAa / LIA',         color: C.green    },
    { key:'fer',   label:'Feriados',            color: C.red      },
    { key:'hoje',  label:'Hoje',                color: C.cyan     },
  ];
  return (
    <div style={{
      display:'flex', alignItems:'center', flexWrap:'wrap', gap:8,
      padding:'14px 16px',
      background:C.bgPanel,
      border:`1px solid ${C.border}`, borderRadius:12, marginBottom:18,
    }}>
      <span style={{ fontSize:10, fontWeight:700, letterSpacing:'0.1em', color:C.gray,
        fontFamily:"'JetBrains Mono', monospace", textTransform:'uppercase', marginRight:6 }}>
        Legenda · clique para filtrar
      </span>
      {items.map(it => {
        const active = filter === it.key;
        return (
          <button key={it.key} className="ce-no-print"
            onClick={() => setFilter(active ? null : it.key)}
            style={{
              display:'inline-flex', alignItems:'center', gap:8,
              padding:'6px 12px', borderRadius:20,
              border:`1px solid ${active ? it.color : C.border}`,
              background: active ? `${it.color}1c` : 'rgba(255,255,255,0.02)',
              color: active ? it.color : C.grayLt,
              fontSize:11.5, fontWeight:600, cursor:'pointer',
              transition:'all 0.15s',
              fontFamily:"'Inter', sans-serif",
            }}>
            <span style={{ width:14, height:14, borderRadius:4,
              background:`linear-gradient(135deg, ${it.color}cc, ${it.color}66)`,
              boxShadow: active ? `0 0 8px ${it.color}` : 'none' }}/>
            {it.label}
          </button>
        );
      })}
      <div style={{ flex:1 }}/>
      <button className="ce-no-print" onClick={() => window.print()} style={{
        display:'inline-flex', alignItems:'center', gap:6,
        height:32, padding:'0 14px', borderRadius:8,
        border:`1px solid ${C.cyanBorder}`, background:C.cyanDim,
        color:C.cyan, fontSize:12, fontWeight:600, cursor:'pointer',
        fontFamily:"'Inter', sans-serif",
      }}>
        <PrintIcon style={{ fontSize:14 }}/>
        Imprimir / PDF
      </button>
    </div>
  );
};

// ─── DAY CELL ────────────────────────────────────────────────
const Day: React.FC<{ d: DayCell; today: Date; weekSE: number | null; filter: string | null }> =
  ({ d, today, weekSE, filter }) => {
  const isToday = !d.isPad && d.monthIdx === today.getMonth() && d.day === today.getDate() && today.getFullYear() === 2026;
  const hol     = !d.isPad ? HOLIDAYS[`${d.monthIdx}-${d.day}`] : '';
  const isLiraa = !d.isPad && weekSE !== null && LIRAA_WEEKS.has(weekSE);
  const isPE    = !d.isPad && PE_DATES.has(`${d.monthIdx}-${d.day}`);

  let dim = false;
  if (filter) {
    if (filter === 'pe'    && !isPE)    dim = true;
    if (filter === 'liraa' && !isLiraa) dim = true;
    if (filter === 'fer'   && !hol)     dim = true;
    if (filter === 'hoje'  && !isToday) dim = true;
  }

  let bg        = 'transparent';
  let border    = `1px solid ${C.borderMid}`;
  let color     = d.isPad ? 'rgba(255,255,255,0.18)' : C.white;
  let boxShadow = 'none';
  let weight    = 500;

  if (!d.isPad) {
    if (isLiraa) { bg = `linear-gradient(135deg, ${C.green}33, ${C.green}10)`;      border = `1px solid ${C.green}66`;    color = '#d8f5e3'; }
    if (isPE)    { bg = `linear-gradient(135deg, ${C.lavender}33, ${C.lavender}10)`; border = `1px solid ${C.lavender}66`; color = '#e8e0ff'; }
    if (hol)     { bg = `linear-gradient(135deg, ${C.red}33, ${C.red}10)`;           border = `1px solid ${C.red}66`;      color = '#fdd'; }
    if (isToday) {
      bg = `linear-gradient(135deg, ${C.cyan}, #007a8a)`;
      border    = `2px solid ${C.cyan}`;
      boxShadow = `0 0 0 3px rgba(3,11,22,1), 0 0 0 4px ${C.cyan}, 0 0 24px ${C.cyan}aa, 0 6px 16px rgba(0,184,204,0.4)`;
      color     = '#021018';
      weight    = 900;
    }
  }

  return (
    <div className="ce-cal-day"
      title={isToday ? 'HOJE' : (hol || (isLiraa ? 'LIRAa/LIA' : isPE ? 'Ponto Estratégico' : ''))}
      style={{
        position:'relative', aspectRatio:'1 / 1', minHeight:32,
        background:bg, border, borderRadius:6,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontFamily:"'JetBrains Mono', monospace", fontVariantNumeric:'tabular-nums',
        fontSize: isToday ? 16 : 13,
        fontWeight:weight, color, opacity: dim ? 0.18 : 1,
        transition:'opacity 0.2s', boxShadow,
        animation: isToday ? 'ce-todayRing 2.2s ease-in-out infinite' : undefined,
        transform: isToday ? 'scale(1.08)' : 'none',
        zIndex: isToday ? 5 : 1,
        textShadow: isToday ? '0 1px 0 rgba(255,255,255,0.35)' : 'none',
        letterSpacing: isToday ? '-0.02em' : '0',
      }}>
      {d.isPad ? (
        <span style={{ fontSize:9, color:'rgba(255,255,255,0.22)' }}>
          {d.day}-{MONTHS[d.monthIdx].toLowerCase()}
        </span>
      ) : d.day}
      {isToday && (
        <span style={{
          position:'absolute', top:-10, left:'50%',
          transform:'translateX(-50%)',
          padding:'2px 7px', borderRadius:4,
          background:C.cyan, color:'#021018',
          fontSize:8.5, fontWeight:900, letterSpacing:'0.12em',
          fontFamily:"'JetBrains Mono', monospace",
          boxShadow:`0 0 10px ${C.cyan}aa, 0 2px 4px rgba(0,0,0,0.5)`,
          animation:'ce-todayBadgePulse 2.2s ease-in-out infinite',
          whiteSpace:'nowrap', zIndex:6,
        }}>HOJE</span>
      )}
      {isToday && (
        <span style={{
          position:'absolute', top:-3, right:-3,
          width:0, height:0,
          borderLeft:`6px solid transparent`,
          borderBottom:`6px solid ${C.orange}`,
          filter:`drop-shadow(0 0 4px ${C.orange})`,
          animation:'ce-todayCorner 1.6s ease-in-out infinite',
        }}/>
      )}
      {!d.isPad && !isToday && (isLiraa || isPE || hol) && (
        <span style={{
          position:'absolute', bottom:3, right:4,
          width:4, height:4, borderRadius:'50%',
          background: hol ? C.red : isPE ? C.lavender : C.green,
          boxShadow:`0 0 4px currentColor`,
        }}/>
      )}
    </div>
  );
};

// ─── MONTH CARD ──────────────────────────────────────────────
const MonthCard: React.FC<{ mIdx: number; today: Date; currentSE: number | null; filter: string | null }> =
  ({ mIdx, today, currentSE, filter }) => {
  const weeks = useMemo(() => buildMonth(mIdx), [mIdx]);
  const cy = cycleOf(mIdx);
  const isFirstOfCycle = cy.months[0] === mIdx;
  const dows = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  return (
    <div className="ce-cal-card" style={{
      position:'relative',
      background:`linear-gradient(180deg, ${C.bgPanel} 0%, #050e1c 100%)`,
      border:`1px solid ${C.border}`,
      borderRadius:14, overflow:'hidden',
      boxShadow:'0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)',
      animation:'ce-fadeIn 0.4s ease',
    }}>
      <div style={{ height:3, background:`linear-gradient(90deg, ${cy.color}, ${cy.color}33)`, opacity:0.9 }}/>
      <div style={{ padding:'14px 16px 10px', display:'flex', alignItems:'flex-end', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
          <span style={{ fontSize:20, fontWeight:800, color:C.white, letterSpacing:'-0.02em', fontFamily:"'Inter', sans-serif" }}>
            {MONTHS_LONG[mIdx]}
          </span>
          <span style={{ fontSize:11, color:C.gray, fontFamily:"'JetBrains Mono', monospace" }}>2026</span>
        </div>
        {isFirstOfCycle && (
          <div style={{
            display:'inline-flex', alignItems:'center', gap:6,
            padding:'3px 9px', borderRadius:6,
            background:`${cy.color}1a`, border:`1px solid ${cy.color}55`,
            fontSize:10, fontWeight:700, color:cy.color,
            fontFamily:"'JetBrains Mono', monospace", letterSpacing:'0.06em',
          }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:cy.color, boxShadow:`0 0 4px ${cy.color}` }}/>
            {cy.label.toUpperCase()}
          </div>
        )}
      </div>
      <div className="ce-cal-grid" style={{ padding:'4px 14px 14px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'34px repeat(7, 1fr)', gap:5, marginBottom:6 }}>
          <div style={{
            fontSize:9, fontWeight:700, letterSpacing:'0.06em',
            color:C.cyan, fontFamily:"'JetBrains Mono', monospace",
            textAlign:'center', padding:'4px 0',
            background:`${C.cyan}10`, borderRadius:4,
          }}>SE</div>
          {dows.map((d, i) => (
            <div key={d} style={{
              fontSize:10, fontWeight:700, letterSpacing:'0.06em',
              color: (i === 0 || i === 6) ? C.orange : C.grayLt,
              fontFamily:"'JetBrains Mono', monospace",
              textAlign:'center', padding:'4px 0',
              borderBottom:`1px solid ${C.border}`,
            }}>{d}</div>
          ))}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          {weeks.map((w, wi) => {
            const seActive = w.se !== null && w.se === currentSE;
            return (
              <div key={wi} style={{ display:'grid', gridTemplateColumns:'34px repeat(7, 1fr)', gap:5 }}>
                <div style={{
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:"'JetBrains Mono', monospace",
                  fontSize:11, fontWeight:700,
                  color: seActive ? C.cyan : C.gray,
                  background: seActive ? `${C.cyan}1a` : 'rgba(255,255,255,0.02)',
                  border: seActive ? `1px solid ${C.cyan}66` : `1px solid ${C.borderMid}`,
                  borderRadius:6, letterSpacing:'0.04em',
                }}>{w.se || '—'}</div>
                {w.days.map((d, di) => (
                  <Day key={di} d={d} today={today} weekSE={w.se} filter={filter}/>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── CYCLE STRIP ─────────────────────────────────────────────
const CycleStrip: React.FC = () => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:8, marginBottom:18 }}>
    {CYCLES.map(cy => (
      <div key={cy.n} style={{
        position:'relative', overflow:'hidden',
        padding:'10px 14px', borderRadius:10,
        background:'rgba(255,255,255,0.02)',
        border:`1px solid ${C.border}`,
        boxShadow:'inset 0 1px 0 rgba(255,255,255,0.02)',
      }}>
        <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, background:cy.color, opacity:0.6 }}/>
        <div style={{ marginLeft:6 }}>
          <span style={{ fontSize:13, fontWeight:800, color:C.white, fontFamily:"'JetBrains Mono', monospace", letterSpacing:'-0.01em' }}>
            {cy.n}° Ciclo
          </span>
        </div>
        <div style={{ marginLeft:6, marginTop:3, fontSize:10, color:C.gray, fontFamily:"'JetBrains Mono', monospace" }}>
          {MONTHS[cy.months[0]]} – {MONTHS[cy.months[1]]} · {cy.se}
        </div>
      </div>
    ))}
  </div>
);

// ─── NOTES ───────────────────────────────────────────────────
const Notes: React.FC = () => (
  <div style={{
    marginTop:28, padding:'20px 22px',
    background:C.bgPanel,
    border:`1px solid ${C.border}`, borderRadius:12,
    borderLeft:`3px solid ${C.cyan}`,
  }}>
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6.5" stroke={C.cyan} strokeWidth="1.3"/>
        <path d="M8 5v3.5M8 11v.5" stroke={C.cyan} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.1em', color:C.cyan, textTransform:'uppercase', fontFamily:"'JetBrains Mono', monospace" }}>
        Notas operacionais
      </span>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
      <div style={{ fontSize:12.5, lineHeight:1.6, color:C.grayLt, fontFamily:"'Inter', sans-serif" }}>
        Em localidades monitoradas por armadilhas faz-se necessário um{' '}
        <strong style={{color:C.white}}>mínimo de dois levantamentos de índice larvário anualmente</strong>,
        um a cada semestre, conforme calendário.
      </div>
      <div style={{ fontSize:12.5, lineHeight:1.6, color:C.grayLt, fontFamily:"'Inter', sans-serif" }}>
        Municípios <strong style={{color:C.white}}>estratificados</strong>: trabalhar visitas domiciliares no formato LI+T e realizar LI em 1/5 ou 1/10 dos imóveis, conforme capacidade operacional. Áreas prioritárias: até 100% dos imóveis.
      </div>
      <div style={{ fontSize:12.5, lineHeight:1.6, color:C.grayLt, fontFamily:"'Inter', sans-serif" }}>
        Municípios <strong style={{color:C.white}}>não estratificados</strong>: LI+T em 100% dos imóveis e LI em 1/5 ou 1/10, conforme capacidade.
      </div>
      <div style={{ fontSize:12.5, lineHeight:1.6, color:C.grayLt, fontFamily:"'Inter', sans-serif" }}>
        Áreas <strong style={{color:C.white}}>não prioritárias</strong>: visitas orientadas por notificações e pelo aumento de indicadores entomológicos.
      </div>
    </div>
    <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${C.border}`, fontSize:10.5, color:C.gray, fontFamily:"'JetBrains Mono', monospace", letterSpacing:'0.03em' }}>
      Fonte: SESAB · DIVEP · COOTV — Coordenação de Vigilância Epidemiológica de Doenças Transmitidas por Vetores e Outras Antropozoonoses.
    </div>
  </div>
);

// ─── ROOT ────────────────────────────────────────────────────
interface Props { onVoltar: () => void; }

const CalendarioEpidemiologico: React.FC<Props> = ({ onVoltar }) => {
  const today     = new Date();
  const currentSE = today.getFullYear() === 2026 ? seFor(today.getMonth(), today.getDate()) : null;
  const [filter, setFilter] = useState<string | null>(null);

  return (
    <Box sx={{ minHeight:'100vh', position:'relative', backgroundColor: C.bg }}>
      <style>{ANIMATIONS_CSS}</style>
      <Background/>
      <div style={{ position:'relative', zIndex:1 }}>
        <PageHeader today={today} currentSE={currentSE} onVoltar={onVoltar}/>
        <main style={{ maxWidth:1480, margin:'0 auto', padding:'24px 32px 48px' }}>
          <CycleStrip/>
          <Legend filter={filter} setFilter={setFilter}/>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:18 }}>
            {MONTHS.map((_, i) => (
              <MonthCard key={i} mIdx={i} today={today} currentSE={currentSE} filter={filter}/>
            ))}
          </div>
          <Notes/>
        </main>
        <div style={{ textAlign:'center', padding:'24px 40px', borderTop:`1px solid ${C.border}`, marginTop:24 }}>
          <span style={{ fontSize:11, color:C.gray, fontFamily:"'Inter', sans-serif" }}>
            © 2026 Tovis — Desenvolvido por{' '}
            <span style={{ color:C.cyan, fontWeight:500 }}>Walber Reis</span>
          </span>
        </div>
      </div>
    </Box>
  );
};

export default CalendarioEpidemiologico;
